import React, { useEffect, useRef, useState } from "react";
import { Download, Smartphone, Share, Plus, X, Apple } from "lucide-react";

/**
 * Reusable "Install App" button for PWA installation.
 * - Hides if app is already installed (standalone display-mode).
 * - On supported browsers (Chrome/Edge/Android), triggers native install prompt.
 * - On iOS Safari, shows a help modal with "Share → Add to Home Screen" steps.
 *
 * Props:
 *   variant: "navy" (default, gold-on-navy header pill) | "outline" (navy-on-white) | "ghost"
 *   size: "sm" | "md"
 *   className: extra classes
 *   label: button label (default "Install App")
 */
export default function InstallAppButton({
  variant = "navy",
  size = "md",
  className = "",
  label = "Install App",
}) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const deferredRef = useRef(null);

  const isIos = typeof window !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent || "") && !window.MSStream;
  const isSafari = typeof window !== "undefined" && /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent || "");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const standaloneMm = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
    const standaloneIos = window.navigator && window.navigator.standalone === true;
    if (standaloneMm || standaloneIos) {
      setInstalled(true);
      return;
    }
    const onBeforeInstall = (e) => {
      e.preventDefault();
      deferredRef.current = e;
      setDeferred(e);
    };
    const onInstalled = () => {
      deferredRef.current = null;
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  async function handleClick() {
    if (deferred) {
      deferred.prompt();
      try {
        await deferred.userChoice;
      } catch (_) {
        /* noop */
      }
      setDeferred(null);
      return;
    }
    // No deferred prompt — show iOS help (or generic instructions on unsupported browsers)
    setShowIosHelp(true);
  }

  // Style maps
  const sizeCls = size === "sm" ? "px-2.5 py-1.5 text-[11px]" : "px-3 py-2 text-xs";
  let variantCls = "";
  if (variant === "navy") {
    variantCls = "bg-ncc-gold text-ncc-navy hover:bg-ncc-gold2";
  } else if (variant === "outline") {
    variantCls = "border border-[#1e3a8a]/30 bg-white text-[#1e3a8a] hover:bg-[#eef2ff]";
  } else if (variant === "ghost") {
    variantCls = "bg-white/10 text-white border border-white/30 hover:bg-white/20";
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid="install-app-btn"
        aria-label="Install App"
        className={`inline-flex items-center gap-2 rounded-md font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5 ${sizeCls} ${variantCls} ${className}`}
      >
        <Download className="h-4 w-4" />
        <span className="hidden sm:inline">{label}</span>
      </button>

      {showIosHelp && (
        <InstallHelpModal isIos={isIos || isSafari} onClose={() => setShowIosHelp(false)} />
      )}
    </>
  );
}

function InstallHelpModal({ isIos, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="install-help-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl border-2 border-ncc-gold bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b-2 border-ncc-gold bg-ncc-navy px-5 py-3 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-ncc-gold" />
            <h3 className="font-heading text-base font-bold uppercase tracking-wider text-white">
              Install on Your Device
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-white/80 hover:bg-white/10 hover:text-white"
            data-testid="install-help-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {isIos ? (
            <>
              <div className="flex items-start gap-3 rounded-lg bg-[#eef2ff] p-3">
                <Apple className="mt-0.5 h-5 w-5 shrink-0 text-[#1e3a8a]" />
                <p className="text-sm text-slate-700">
                  On iPhone/iPad, install this app from <span className="font-bold">Safari</span>.
                </p>
              </div>
              <ol className="space-y-3 text-sm text-slate-800">
                <li className="flex items-start gap-3">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ncc-gold text-xs font-bold text-ncc-navy">1</span>
                  <span className="flex flex-wrap items-center gap-1">
                    Tap the <Share className="inline h-4 w-4 text-[#1e3a8a]" /> <span className="font-semibold">Share</span> button at the bottom of Safari.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ncc-gold text-xs font-bold text-ncc-navy">2</span>
                  <span className="flex flex-wrap items-center gap-1">
                    Scroll down and tap <Plus className="inline h-4 w-4 text-[#1e3a8a]" /> <span className="font-semibold">Add to Home Screen</span>.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ncc-gold text-xs font-bold text-ncc-navy">3</span>
                  <span>Tap <span className="font-semibold">Add</span> in the top-right corner. The NCC-SWMS icon will appear on your home screen.</span>
                </li>
              </ol>
            </>
          ) : (
            <>
              <div className="flex items-start gap-3 rounded-lg bg-[#eef2ff] p-3">
                <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-[#1e3a8a]" />
                <p className="text-sm text-slate-700">
                  Your browser doesn&apos;t currently show an install prompt. You can still install the app manually.
                </p>
              </div>
              <ol className="space-y-3 text-sm text-slate-800">
                <li className="flex items-start gap-3">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ncc-gold text-xs font-bold text-ncc-navy">1</span>
                  <span>Open this site in <span className="font-semibold">Chrome</span> or <span className="font-semibold">Edge</span> (desktop or Android).</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ncc-gold text-xs font-bold text-ncc-navy">2</span>
                  <span>Open the browser menu (⋮) and choose <span className="font-semibold">Install app</span> or <span className="font-semibold">Add to Home screen</span>.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ncc-gold text-xs font-bold text-ncc-navy">3</span>
                  <span>Confirm the install. The NCC-SWMS app will be added to your device.</span>
                </li>
              </ol>
            </>
          )}

          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
            Installed apps work offline, support camera uploads, and launch full-screen — just like a native app.
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            data-testid="install-help-ok"
            className="rounded-md bg-ncc-navy px-4 py-2 text-xs font-bold uppercase tracking-wider text-white transition-all hover:-translate-y-0.5 hover:bg-[#152a63]"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
